import { BarChart, Database, Brain, Search, Zap, MessageSquare, AlertTriangle, Wrench, Users, Target } from 'lucide-react';
import type { Product } from './types';

export const dataAnalyticsBIProducts: Product[] = [
  {
    id: 'recorded-future-intelligence',
    title: 'Recorded Future',
    subtitle: 'Threat Intelligence Platform',
    description: 'Complete intelligence platform providing active security recommendations used by 1,900+ clients in 80+ countries for threat detection.',
    icon: Search,
    image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?auto=format&fit=crop&w=800&q=80',
    category: 'Data Analytics & BI',
    status: 'Available',
    businessTypes: ['#threat-intelligence', '#cybersecurity', '#risk-management', '#analytics', '#enterprise'],
    features: [
      'Real-time threat intelligence feeds',
      'Predictive security analytics',
      'Dark web monitoring',
      'Vulnerability intelligence',
      'IOC enrichment and correlation',
      'Custom threat hunting capabilities',
      'UK cyber security standards',
      'GDPR threat data compliance'
    ],
    pricing: { setup: 64000, monthly: 2944 },
    color: 'from-red-500 to-pink-500'
  },
  {
    id: 'embedded-analytics-platform',
    title: 'Embedded Analytics Platform',
    subtitle: 'White-Label BI Solution',
    description: 'Allows SaaS companies to integrate advanced data visualizations and reporting capabilities directly into their applications.',
    icon: BarChart,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80',
    category: 'Data Analytics & BI',
    status: 'Available',
    businessTypes: ['#embedded-analytics', '#white-label', '#saas', '#data-visualization', '#reporting'],
    features: [
      'White-label dashboard embedding',
      'Custom branding and themes',
      'Interactive data visualisations',
      'Multi-tenant architecture',
      'API-driven data connections',
      'Self-service analytics for end users',
      'GDPR data protection compliance',
      'UK business intelligence standards'
    ],
    pricing: { setup: 38400, monthly: 1766 },
    color: 'from-blue-500 to-indigo-500'
  },
  {
    id: 'predictive-analytics-workbench',
    title: 'Predictive Analytics Workbench',
    subtitle: 'ML Model Development Platform',
    description: 'Enables data scientists to build, deploy, and monitor machine learning models without infrastructure management complexity.',
    icon: Brain,
    image: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=800&q=80',
    category: 'Data Analytics & BI',
    status: 'Available',
    businessTypes: ['#predictive-analytics', '#machine-learning', '#data-science', '#modeling', '#forecasting'],
    features: [
      'Drag-and-drop model building',
      'Automated feature engineering',
      'Model deployment and versioning',
      'Performance monitoring and drift detection',
      'Collaborative workspace',
      'Integration with popular ML libraries'
    ],
    pricing: { setup: 30720, monthly: 1413 },
    color: 'from-purple-500 to-pink-500'
  },
  {
    id: 'data-catalog-governance',
    title: 'Data Catalog & Governance',
    subtitle: 'Enterprise Data Management',
    description: 'Automates data discovery, classification, and governance across multiple sources and systems with comprehensive lineage tracking.',
    icon: Database,
    image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?auto=format&fit=crop&w=800&q=80',
    category: 'Data Analytics & BI',
    status: 'Available',
    businessTypes: ['#data-governance', '#data-catalog', '#compliance', '#metadata-management', '#enterprise'],
    features: [
      'Automated data discovery and cataloging',
      'Data lineage and impact analysis',
      'Privacy and compliance monitoring',
      'Data quality assessment',
      'Access control and permissions',
      'Glossary and documentation management'
    ],
    pricing: { setup: 51200, monthly: 2355 },
    color: 'from-green-500 to-emerald-500'
  },
  {
    id: 'realtime-streaming-analytics',
    title: 'Real-Time Streaming Analytics',
    subtitle: 'Stream Processing Platform',
    description: 'Processes and analyses data streams in real-time to detect patterns, anomalies, and trigger immediate automated actions for UK business requirements.',
    icon: Zap,
    image: 'https://images.unsplash.com/photo-1518186285589-2f7649de83e0?auto=format&fit=crop&w=800&q=80',
    category: 'Data Analytics & BI',
    status: 'Available',
    businessTypes: ['#streaming-analytics', '#real-time', '#event-processing', '#iot', '#monitoring'],
    features: [
      'High-throughput stream processing',
      'Complex event pattern detection',
      'Real-time alerting and notifications',
      'Windowed aggregations',
      'Integration with IoT and sensors',
      'Scalable distributed architecture'
    ],
    pricing: { setup: 46080, monthly: 2120 },
    color: 'from-orange-500 to-red-500'
  },
  {
    id: 'natural-language-query',
    title: 'Natural Language Query Interface',
    subtitle: 'Conversational Analytics',
    description: 'Allows users to ask questions about their data in plain language and receive answers with automated visualizations.',
    icon: MessageSquare,
    image: 'https://images.unsplash.com/photo-1531297484001-80022131f5a1?auto=format&fit=crop&w=800&q=80',
    category: 'Data Analytics & BI',
    status: 'Available',
    businessTypes: ['#natural-language', '#conversational-analytics', '#self-service-bi', '#nlp', '#data-democratization'],
    features: [
      'Natural language query processing',
      'Automated chart and graph generation',
      'Context-aware follow-up questions',
      'Voice and text input support',
      'Smart query suggestions',
      'Business context understanding'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-cyan-500 to-blue-500'
  },
  {
    id: 'anomaly-detection-system',
    title: 'Anomaly Detection System',
    subtitle: 'Intelligent Monitoring Platform',
    description: 'Automatically identifies unusual patterns in time-series data that may indicate problems, opportunities, or security threats.',
    icon: AlertTriangle,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80',
    category: 'Data Analytics & BI',
    status: 'Available',
    businessTypes: ['#anomaly-detection', '#monitoring', '#alerting', '#machine-learning', '#operations'],
    features: [
      'Unsupervised anomaly detection',
      'Multi-dimensional pattern analysis',
      'Customizable sensitivity thresholds',
      'Root cause analysis assistance',
      'Integration with monitoring tools',
      'Historical trend comparison'
    ],
    pricing: { setup: 40960, monthly: 1884 },
    color: 'from-yellow-500 to-orange-500'
  },
  {
    id: 'data-preparation-platform',
    title: 'Data Preparation Platform',
    subtitle: 'Automated Data Pipeline',
    description: 'Cleans, transforms, and enriches raw data to make it analysis-ready with minimal manual effort and maximum accuracy.',
    icon: Wrench,
    image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=800&q=80',
    category: 'Data Analytics & BI',
    status: 'Available',
    businessTypes: ['#data-preparation', '#etl', '#data-cleaning', '#transformation', '#pipeline'],
    features: [
      'Automated data profiling and quality assessment',
      'Smart data transformation suggestions',
      'Data deduplication and standardization',
      'Schema mapping and validation',
      'Error handling and data lineage',
      'Schedule-based pipeline execution'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-teal-500 to-green-500'
  },
  {
    id: 'collaborative-data-science',
    title: 'Collaborative Data Science Platform',
    subtitle: 'Team Analytics Workspace',
    description: 'Enables data science teams to collaborate on projects with version control, reproducibility, and knowledge sharing features.',
    icon: Users,
    image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?auto=format&fit=crop&w=800&q=80',
    category: 'Data Analytics & BI',
    status: 'Available',
    businessTypes: ['#data-science', '#collaboration', '#version-control', '#jupyter', '#team-workspace'],
    features: [
      'Collaborative notebook environment',
      'Version control for data science projects',
      'Shared datasets and model registry',
      'Experiment tracking and comparison',
      'Code review and commenting',
      'Resource sharing and scheduling'
    ],
    pricing: { setup: 30000, monthly: 1380 },
    color: 'from-violet-500 to-purple-500'
  },
  {
    id: 'unified-metrics-platform',
    title: 'Unified Metrics Platform',
    subtitle: 'Enterprise KPI Management',
    description: 'Defines, calculates, and distributes key business metrics consistently across the organisation with governance controls for UK compliance.',
    icon: Target,
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=800&q=80',
    category: 'Data Analytics & BI',
    status: 'Available',
    businessTypes: ['#kpi-management', '#metrics', '#business-intelligence', '#governance', '#enterprise'],
    features: [
      'Centralized metric definitions',
      'Automated calculation and distribution',
      'Metric lineage and documentation',
      'Access control and governance',
      'Real-time metric monitoring',
      'Custom alerting and notifications'
    ],
    pricing: { setup: 38400, monthly: 1766 },
    color: 'from-indigo-500 to-blue-500'
  }
];